import React from 'react';
import { Modal, Spin, Upload, message } from 'antd';
import { CloudUploadOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';

import Config from '../../config/Config';
import WidgetAjax from '../../widget/WidgetAjax';

import ResumeHeader from './components/ResumeHeader';
import ResumeSection from './components/ResumeSection';
import SingleSectionView from './com/SingleSectionView';

import BasicInfoModule from './modules/BasicInfoModule';
import IntentionModule from './modules/IntentionModule';
import WorkExperienceModule from './modules/WorkExperienceModule';
import EducationAbilityModule from './modules/EducationAbilityModule';

import {
  resumeSections,
  advantageSection,
  statusSection,
} from './configs/sectionConfig';
import {
  normalizeOptions,
  normalizeJobTree,
  buildSimpleApplicantParams,
} from './utils/resumeTransforms';

import './resumeNew.scss';

const basePath = Config.getBasePath();

/**
 * 字典维度码 → 选项 key。沿用 hooks/useResumeOptions.js 的 dimensionMap,
 * 在本类里以 class field 形式保存(非 state,避免每次 setState 重建引用)。
 */
const dimensionMap = {
  D301: 'eduOptions',
  D305: 'plstandOptions',
  D306: 'identityOptions',
  D307: 'statusOptions',
  D311: 'skilledOptions',
  D314: 'languageOptions',
};


class ResumeNewPage extends React.Component {
  
  formRef = React.createRef();

  sectionRefs = {};

  sectionRefCallbacks = {};

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      applicantId: undefined,
      // 各模块自行从 resumeData.raw / resumeData.info 解析各自的数据格式,
      // 主页面只透传 /applicant/resume_info 返回的原始数据,不再做字段归一化.
      resumeData: { raw: {}, info: {} },
      options: {
        eduOptions: [],
        plstandOptions: [],
        identityOptions: [],
        statusOptions: [],
        skilledOptions: [],
        languageOptions: [],
        areaList: [],
        jobTypeList: [],
      },
      activeId: undefined,
      simpleEditingId: undefined,
      resumeFile: undefined,
      uploading: false,
    };

    // 一次性为每个 section.id 生成稳定的 ref 回调,render 时直接按 id 取
    resumeSections.forEach((section) => {
      this.sectionRefCallbacks[section.id] = (el) => {
        if (el) {
          this.sectionRefs[section.id] = el;
        } else {
          delete this.sectionRefs[section.id];
        }
      };
    });
  }

  componentDidMount() {
    this.loadResume();
    this.loadOptions();
  }

  // ==================== 数据加载 ====================

  loadResume = () => {
    this.setState({ loading: true });
    WidgetAjax.ajax({
      url: `${basePath}/applicant/resume_info`,
      params: {},
      callback: (res) => {
        if (!res || res.status !== 0) {
          message.error((res && res.message) || '简历加载失败');
          this.setState({ loading: false });
          return;
        }
        // 后端 `/applicant/resume_info` 的载荷是单条简历对象,字段直接挂在 `result`
        // (不是 results 数组). 主页面只透传这一份原始数据,
        // 不做归一化或字段派生 — 各模块自行从 resumeData.raw 取自己关心的键
        // (intentions / educations / occupations / skills / certificates /
        // languages / works 等).
        const raw = res.result || {};
        const applicantId = raw.id !== undefined && raw.id !== null ? String(raw.id) : undefined;
        this.setState({
          loading: false,
          applicantId,
          resumeData: { raw, info: raw },
          resumeFile: raw.RESUME,
        });
      },
    });
  };

  /**
   * 拉取字典 + 区域 + 岗位类型:
   *   - 六个维度 (D301/D305/D306/D307/D311/D314) → 字典
   *   - /recruitment/area         → 区域树
   *   - /recruitment/job_type     → 岗位类型树(默认 parent_code='0')
   * - 每个请求单独回调,失败仅 toast,不阻塞其它加载。
   */
  loadOptions = () => {
    Object.keys(dimensionMap).forEach((code) => this.loadDimension(code));
    this.loadAreaList();
    this.reloadJobTypes();
  };

  loadDimension = (code) => {
    WidgetAjax.ajax({
      url: `${basePath}/widget/dimension`,
      params: { dimension: code },
      callback: (res) => {
        if (res && res.status === 0) {
          const key = dimensionMap[code];
          const values = (res.results && res.results[0]) ? res.results[0].values : [];
          this.setState((prev) => ({
            options: { ...prev.options, [key]: normalizeOptions(values) },
          }));
          return;
        }
        message.warning(`维度 ${code} 加载失败`);
      },
    });
  };

  loadAreaList = () => {
    WidgetAjax.ajax({
      url: `${basePath}/recruitment/area`,
      callback: (res) => {
        if (res && res.status === 0) {
          const areaList = (res.results && res.results[0]) ? res.results[0].children : [];
          this.setState((prev) => ({
            options: { ...prev.options, areaList },
          }));
          return;
        }
        message.warning('地区数据加载失败');
      },
    });
  };

  /**
   * 暴露给 IntentionModule / WorkExperienceModule 的 JobSelectModal 搜索回调。
   * 也用于主页面初始化加载。
   */
  reloadJobTypes = (word) => {
    WidgetAjax.ajax({
      url: `${basePath}/recruitment/job_type`,
      params: { parent_code: '0', word },
      callback: (res) => {
        if (res && res.status === 0) {
          const jobTypeList = normalizeJobTree(res.results || []);
          this.setState((prev) => ({
            options: { ...prev.options, jobTypeList },
          }));
          return;
        }
        message.warning('岗位类型加载失败');
      },
    });
  };

  // ==================== 个人优势 / 求职状态 ====================

  /**
   * 进入个人优势 / 求职状态 编辑态:
   *   - 一次性快照 initialValues,避免后续 props 变化导致 antd Form 重置
   *   - simpleEditingId 决定显示哪一个编辑表单
   */
  handleSimpleEdit = (id) => {
    const { resumeData } = this.state;
    const baseInfo = resumeData.info || {};
    this.simpleInitialValues = id === advantageSection.id
      ? { ADVANTAGE: baseInfo.ADVANTAGE }
      : { STATUS: baseInfo.STATUS };
    this.setState({ simpleEditingId: id });
  };

  handleSimpleCancel = () => {
    this.simpleInitialValues = null;
    this.setState({ simpleEditingId: undefined });
  };

  /**
   * 个人优势 / 求职状态 保存入口:
   *   - 走 /applicant/update,参数经 buildSimpleApplicantParams 构造
   *   - 成功 → message.success + 关闭编辑 + reloadResume(刷新整个简历信息)
   *   - 失败 → toast 后端 message,**保留编辑态**便于用户继续修改
   */
  saveSimpleSection = (values) => {
    const params = buildSimpleApplicantParams(
      this.state.resumeData.info,
      values
    );
    WidgetAjax.ajax({
      url: `${basePath}/applicant/update`,
      params,
      callback: (res) => {
        if (res && res.status === 0) {
          message.success('保存成功');
          this.simpleInitialValues = null;
          this.setState({ simpleEditingId: undefined });
          this.loadResume();
          return;
        }
        message.error((res && res.message) || '保存失败');
      },
    });
  };

  // ==================== 简历文件(附件简历) ====================

  handleResumeDownload = () => {
    WidgetAjax.exportFile({
      url: `${basePath}/applicant/resume_export`,
      params: { id: this.state.applicantId, type: 2, fileName: '简历.pdf' },
    });
  };

  handleResumeDelete = () => {
    Modal.confirm({
      title: '确认删除该简历?',
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        WidgetAjax.ajax({
          url: `${basePath}/applicant/resume_remove`,
          params: { id: this.state.applicantId },
          callback: (res) => {
            if (res && res.status === 0) {
              message.success('删除成功');
              this.loadResume();
              return;
            }
            message.error((res && res.message) || '删除失败');
          },
        });
      },
    });
  };

  /**
   * antd Upload.beforeUpload:false 阻止其内置上传,改走 WidgetAjax.uploadFile。
   * 校验 → FormData → resume_import → 成功 reload / 失败 toast。
   */
  handleResumeBeforeUpload = (file) => {
    if (file.type !== 'application/pdf') {
      message.warning('请上传 PDF 文件!');
      return false;
    }
    this.setState({ uploading: true });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('id', this.state.applicantId);
    WidgetAjax.uploadFile({
      url: `${basePath}/applicant/resume_import`,
      formData,
      callback: (res) => {
        this.setState({ uploading: false });
        if (res && res.status === 0) {
          message.success('上传成功');
          this.loadResume();
          return;
        }
        message.error((res && res.message) || '上传失败');
      },
    });
    return false;
  };

  // ==================== 目录滚动 ====================


  scrollToSection = (id) => {
    let node = this.sectionRefs[id];
    if (!node && typeof document !== 'undefined' && document.getElementById) {
      node = document.getElementById(id);
    }
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    this.setState({ activeId: id });
  };

  // ==================== 渲染 ====================

  render() {
    const {
      loading, applicantId, resumeData, options,
      simpleEditingId, activeId, resumeFile, uploading,
    } = this.state;

    const advantageEditing = simpleEditingId === advantageSection.id;
    const statusEditing = simpleEditingId === statusSection.id;

    return (
      <div className="resume-new-page" component-data={Config.getSourcePath()}>
        <ResumeHeader
          info={resumeData.info}
          loading={loading}
          applicantId={applicantId}
          onUploaded={this.loadResume}
        />

        <div className="resume-new-layout">
          {/* ============= 左侧:附件简历 + 目录 ============= */}
          <aside className="resume-new-sidebar-wrap">
            {/* 附件简历(原 AttachmentUploader 内联,样式保持一致) */}
            <div className="resume-new-attachment">
              <div className="resume-new-attachment-title">附件简历</div>
              {resumeFile ? (
                <div className="resume-new-attachment-card">
                  <div className="resume-new-attachment-card-row">
                    <div className="resume-new-attachment-card-info">
                      <CloudUploadOutlined className="resume-new-attachment-card-icon" />
                      <div className="resume-new-attachment-card-meta">
                        <div className="resume-new-attachment-card-name">简历.pdf</div>
                        <div className="resume-new-attachment-card-hint">已上传</div>
                      </div>
                    </div>
                  </div>
                  <div className="resume-new-attachment-card-actions">
                    <button
                      type="button"
                      className="resume-new-attachment-card-action"
                      onClick={this.handleResumeDownload}
                    >
                      <DownloadOutlined />
                      <span>下载</span>
                    </button>
                    <button
                      type="button"
                      className="resume-new-attachment-card-action is-danger"
                      onClick={this.handleResumeDelete}
                    >
                      <DeleteOutlined />
                      <span>删除</span>
                    </button>
                  </div>
                </div>
              ) : null}
              <Spin spinning={uploading}>
                <Upload
                  name="file"
                  fileList={[]}
                  showUploadList={false}
                  accept="application/pdf"
                  beforeUpload={this.handleResumeBeforeUpload}
                >
                  {!resumeFile ? (
                    <div className="resume-new-attachment-trigger">
                      <CloudUploadOutlined className="resume-new-attachment-trigger-icon" />
                      <div className="resume-new-attachment-trigger-text">点击上传简历</div>
                      <div className="resume-new-attachment-trigger-hint">
                        仅支持 PDF 格式,总大小不超过 20M
                      </div>
                    </div>
                  ) : null}
                </Upload>
              </Spin>
            </div>

            {/* 目录导航:点击 → scrollToSection + setActiveId */}
            <div className="resume-new-nav resume-new-sidebar-nav">
              {resumeSections.map((section) => (
                <button
                  type="button"
                  key={section.id}
                  className={`resume-new-nav-item${activeId === section.id ? ' is-active' : ''}`}
                  onClick={() => this.scrollToSection(section.id)}
                >
                  {section.title}
                </button>
              ))}
            </div>
          </aside>

          {/*
            ============= 右侧:基本信息 + 个人优势 + 求职状态 + 4 个模块 =============
          */}
          <main className="resume-new-main">
            <Spin spinning={loading}>
              {/* 1) 基本信息:BasicInfoModule(自带 formRef,自管编辑态) */}
              <div ref={this.sectionRefCallbacks[resumeSections[0].id]}>
                <BasicInfoModule
                  info={resumeData.info}
                  options={options}
                  applicantId={applicantId}
                  onChanged={this.loadResume}
                />
              </div>

              {/* 2) 个人优势:SingleSectionView + advantageSection 配置;表单句柄 = this.formRef */}
              <div ref={this.sectionRefCallbacks[advantageSection.id]}>
                <ResumeSection
                  section={advantageSection}
                  editing={advantageEditing}
                  onEdit={() => this.handleSimpleEdit(advantageSection.id)}
                >
                  <SingleSectionView
                    section={advantageSection}
                    data={resumeData.info}
                    editing={advantageEditing}
                    options={options}
                    applicantId={applicantId}
                    formRef={this.formRef}
                    initialValues={advantageEditing ? this.simpleInitialValues : undefined}
                    onSubmit={this.saveSimpleSection}
                    onCancel={this.handleSimpleCancel}
                  />
                </ResumeSection>
              </div>

              {/* 3) 求职状态:SingleSectionView + statusSection 配置;表单句柄 = this.formRef */}
              <div ref={this.sectionRefCallbacks[statusSection.id]}>
                <ResumeSection
                  section={statusSection}
                  editing={statusEditing}
                  onEdit={() => this.handleSimpleEdit(statusSection.id)}
                >
                  <SingleSectionView
                    section={statusSection}
                    data={resumeData.info}
                    editing={statusEditing}
                    options={options}
                    applicantId={applicantId}
                    formRef={this.formRef}
                    initialValues={statusEditing ? this.simpleInitialValues : undefined}
                    onSubmit={this.saveSimpleSection}
                    onCancel={this.handleSimpleCancel}
                  />
                </ResumeSection>
              </div>

              {/* 4) 求职意向:IntentionModule(自管 items / selectedJobs / selectedAreas / 选择器) */}
              <IntentionModule
                resumeState={resumeData}
                options={options}
                applicantId={applicantId}
                reloadJobTypes={this.reloadJobTypes}
                onChanged={this.loadResume}
              />

              {/* 5) 工作/实习经历:WorkExperienceModule(自管 editingIndex / adding / 选择器) */}
              <WorkExperienceModule
                resumeState={resumeData}
                options={options}
                applicantId={applicantId}
                reloadJobTypes={this.reloadJobTypes}
                onChanged={this.loadResume}
              />

              {/* 6) 教育 & 能力(教育/技能/证书/语言/作品):EducationAbilityModule */}
              <EducationAbilityModule
                resumeState={resumeData}
                options={options}
                applicantId={applicantId}
                onChanged={this.loadResume}
              />
            </Spin>
          </main>
        </div>
      </div>
    );
  }
}

export default ResumeNewPage;